import { UnifiedConnectionDtoProvider } from '@/api/generated';
import AsanaLogo from '@/assets/third-party-logos/asana/asana.svg?react';
import GoogleDriveLogo from '@/assets/third-party-logos/google/google-drive.svg?react';
import HubspotLogo from '@/assets/third-party-logos/hubspot/hubspot.svg?react';
import MondayLogo from '@/assets/third-party-logos/monday/monday.svg?react';
import SalesforceLogo from '@/assets/third-party-logos/salesforce/salesforce.svg?react';
import SlackLogo from '@/assets/third-party-logos/slack/slack.svg?react';
import RoutesMap from '@/enums/RoutesMap';
import useAccountsFF, { AccountFF } from '@/utils/useAccountFF';
import useLocalStorageFF, { LOCAL_STORAGE_KEYS } from '@/utils/useLocalStorageFF';

export interface UnifiedIntegrationProvider {
  type: string;
  name: string;
  logo: React.ReactNode;
  active: boolean;
  redirectUrl?: string;
  scopes?: string;
}

export interface UnifiedIntegrationCategory {
  name: string;
  providers: UnifiedIntegrationProvider[];
}

export const useUnifiedIntegrationsAuth = (): UnifiedIntegrationCategory[] => {
  const isSlackEnabled = useLocalStorageFF(LOCAL_STORAGE_KEYS.SLACK);
  const isGoogleDriveEnabled = useAccountsFF([AccountFF.Gather_AI, AccountFF.Journey]);
  return [
    {
      name: 'General',
      providers: [
        {
          type: UnifiedConnectionDtoProvider.hubspot,
          name: 'Hubspot',
          logo: <HubspotLogo width="30px" height="30px" />,
          active: true,
          redirectUrl: window.origin + RoutesMap.HubspotAuth,
          scopes:
            'crm_company_read,crm_company_write,crm_contact_read,crm_contact_write,crm_deal_read,crm_deal_write,crm_event_read,crm_event_write'
        },
        ...(isSlackEnabled
          ? [
              {
                type: UnifiedConnectionDtoProvider.slackbot,
                name: 'Slack',
                logo: <SlackLogo width="30px" height="30px" />,
                active: true,
                redirectUrl: window.origin + RoutesMap.SlackAuth,
                scopes: ''
              }
            ]
          : []),

        ...(isGoogleDriveEnabled
          ? [
              {
                type: UnifiedConnectionDtoProvider.googleDrive.toLowerCase(),
                name: 'Google Drive',
                logo: <GoogleDriveLogo width="30px" height="30px" />,
                active: true,
                redirectUrl: window.origin + RoutesMap.GoogleDriveAuth,
                scopes: ''
              }
            ]
          : []),
        {
          type: 'salesforce',
          name: 'Salesforce',
          logo: <SalesforceLogo width="30px" height="30px" />,
          active: false
        },
        {
          type: 'monday',
          name: 'Monday.com',
          logo: <MondayLogo width="30px" height="30px" />,
          active: false
        },
        {
          type: 'asana',
          name: 'Asana',
          logo: <AsanaLogo width="30px" height="30px" />,
          active: false
        }
      ]
    }
  ];
};

export const buildConnectionUrl = (
  provider: UnifiedConnectionDtoProvider,
  redirectUrl: string,
  scopes: string,
  currentUserId?: string
) => {
  const url = new URL(
    `https://api.unified.to/unified/integration/auth/${process.env.VITE_UNIFIED_WORKSPACE_ID}/${provider}`
  );
  url.searchParams.append('redirect', 'true');
  url.searchParams.append('env', `${process.env.VITE_UNIFIED_ENV}`);
  url.searchParams.append('success_redirect', redirectUrl);
  url.searchParams.append('external_xref', currentUserId ?? '');

  if (scopes) {
    url.searchParams.append('scopes', scopes);
  }

  return url.toString();
};
